import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, ArrowLeft, DollarSign, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCurrency, useTheme } from '../App';

const CurrencyConverterPage = () => {
  const { USD_TO_XCG, XCG_TO_USD } = useCurrency();
  const { theme } = useTheme();
  const [amount, setAmount] = useState('100');
  const [fromCurrency, setFromCurrency] = useState('USD');

  const convertedAmount = fromCurrency === 'USD' 
    ? parseFloat(amount || 0) * USD_TO_XCG 
    : parseFloat(amount || 0) * XCG_TO_USD;

  const toCurrency = fromCurrency === 'USD' ? 'XCG' : 'USD';

  const swapCurrencies = () => {
    setFromCurrency(fromCurrency === 'USD' ? 'XCG' : 'USD');
  };

  const quickAmounts = [10, 25, 50, 100, 250, 500, 1000, 5000];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/" className="p-2 rounded-xl hover:bg-secondary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Currency Converter</h1>
            <p className="text-muted-foreground text-sm">USD to XCG Caribbean Guilder</p>
          </div>
        </div>

        {/* Converter Card */}
        <div className="ios-luxury-card p-6 mb-6">
          {/* Exchange Rate Banner */}
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-4 mb-6 border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-500" />
                <span className="font-medium">Current Rate</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-foreground">$1 USD = XCG {USD_TO_XCG.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* From Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-foreground">From</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-muted-foreground">
                {fromCurrency === 'USD' ? '$' : 'XCG'}
              </span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`w-full pr-20 py-4 text-2xl font-bold bg-secondary border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fromCurrency === 'USD' ? 'pl-10' : 'pl-16'}`}
                placeholder="0.00"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-muted-foreground">
                {fromCurrency}
              </span>
            </div>
          </div>

          {/* Swap Button */}
          <div className="flex justify-center my-4">
            <button
              onClick={swapCurrencies}
              className="p-3 bg-primary text-white rounded-full hover:bg-primary/90 transition-all transform hover:scale-105 shadow-lg"
            >
              <ArrowLeftRight className="w-5 h-5" />
            </button>
          </div>

          {/* To Amount */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2 text-foreground">To</label>
            <div className="relative">
              <div className="w-full px-4 py-4 text-2xl font-bold bg-green-500/10 border border-green-500/30 rounded-xl text-green-600 dark:text-green-400">
                {toCurrency === 'USD' ? '$' : 'XCG '}{convertedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-green-600 dark:text-green-400">
                {toCurrency}
              </span>
            </div>
          </div>

          {/* Quick Amounts */}
          <div>
            <label className="block text-sm font-medium mb-3 text-foreground">Quick amounts</label>
            <div className="flex flex-wrap gap-2">
              {quickAmounts.map((quickAmount) => (
                <button
                  key={quickAmount}
                  onClick={() => setAmount(quickAmount.toString())}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    amount === quickAmount.toString()
                      ? 'bg-primary text-white'
                      : 'bg-secondary hover:bg-secondary/80 text-foreground'
                  }`}
                >
                  {fromCurrency === 'USD' ? '$' : 'XCG '}{quickAmount.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="card p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Info className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  <strong>Note:</strong> Rates are approximate and may vary. Always verify with your bank for actual transaction rates.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Common Conversions */}
        <div className="mt-6 card p-6">
          <h3 className="font-semibold mb-4 text-foreground">Common Conversions</h3>
          <div className="grid grid-cols-2 gap-3">
            {[5, 10, 20, 50, 100, 200, 500, 1000].map((val) => (
              <div key={val} className="flex justify-between items-center p-3 bg-secondary rounded-lg">
                <span className="font-medium text-foreground">${val}</span>
                <span className="text-muted-foreground">XCG {(val * USD_TO_XCG).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CurrencyConverterPage;
